import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, ChevronLeft, Layers, Cpu, Globe, Award,
  Scissors, PenTool, Briefcase, ShieldCheck, Play, Plus, Minus,
  Truck, Factory, Package, Ruler, Palette, Zap, Users, Tag,
  Shirt, Clock, X, ZoomIn, ZoomOut, Info
} from 'lucide-react';
import { getHomeData } from '../services/api';

// --- UI Components ---

const SectionTag = ({ children, className = '' }) => (
  <div className={`flex items-center gap-2 mb-6 ${className}`}>
    <div className="w-8 h-[2px] bg-blue-600"></div>
    <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 italic">
      {children}
    </span>
  </div>
);

const BloombergButton = ({ children, to, variant = 'primary', className = '' }) => {
  const base = "px-6 py-3 text-sm md:text-base font-bold uppercase tracking-widest transition-all duration-200 border flex items-center justify-center gap-2 inline-flex";
  const styles = {
    primary: "bg-black text-white border-black hover:bg-zinc-800",
    outline: "bg-transparent text-black border-black hover:bg-black hover:text-white",
    outlineWhite: "bg-transparent text-white border-white hover:bg-white hover:text-black"
  };

  if (to) {
    return <Link to={to} className={`${base} ${styles[variant]} ${className}`}>{children}</Link>;
  }
  return <button className={`${base} ${styles[variant]} ${className}`}>{children}</button>;
};

const StatItem = ({ label, value }) => (
  <div className="border-t border-zinc-300 pt-3 md:pt-4">
    <div className="text-2xl md:text-3xl font-light tracking-tighter text-black mb-1">{value}</div>       
    <div className="text-xs md:text-sm uppercase tracking-wider text-zinc-800 font-bold">{label}</div>    
  </div>
);

const Marquee = () => (
  <div className="bg-blue-600 text-white overflow-hidden py-3 flex relative z-20">
    <div className="flex animate-marquee whitespace-nowrap min-w-full flex-shrink-0 items-center">        
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Manufaktur Premium</span>       
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Kualitas Ekspor</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Teknik Presisi</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Praktik Berkelanjutan</span>    
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Pengadaan Global</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Garansi Produksi</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
    </div>
    <div className="flex animate-marquee whitespace-nowrap min-w-full flex-shrink-0 items-center">        
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Manufaktur Premium</span>       
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Kualitas Ekspor</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Teknik Presisi</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Praktik Berkelanjutan</span>    
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Pengadaan Global</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">Garansi Produksi</span>
      <span className="text-sm font-bold uppercase tracking-[0.2em] mx-8">•</span>
    </div>
  </div>
);

const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox State
  const [mediaLightboxOpen, setMediaLightboxOpen] = useState(false);
  const [mediaLightboxItems, setMediaLightboxItems] = useState([]);
  const [mediaLightboxIndex, setMediaLightboxIndex] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Hero Slider State
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [heroMedia, setHeroMedia] = useState([]);

  // FAQ State
  const [faqOpen, setFaqOpen] = useState(null);

  // --- Modal Handlers (Adapted from Gallery.js) ---
  const closeModal = () => {
    setMediaLightboxOpen(false);
    setShowInfo(false);
    setZoomLevel(1);
    document.body.style.overflow = 'auto';
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (mediaLightboxIndex < mediaLightboxItems.length - 1) {
      setMediaLightboxIndex(prev => prev + 1);
      setZoomLevel(1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (mediaLightboxIndex > 0) {
      setMediaLightboxIndex(prev => prev - 1);
      setZoomLevel(1);
    }
  };

  const toggleInfo = (e) => {
    e.stopPropagation();
    setShowInfo(!showInfo);
  };

  // Keyboard Navigation for Modal
  useEffect(() => {
    if (!mediaLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') handleNext(e);
      if (e.key === 'ArrowLeft') handlePrev(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mediaLightboxOpen, mediaLightboxIndex]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await getHomeData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  const { static: content = {}, gallery = [], articles = [] } = data || {};

  // Hero Media Logic
  const heroImageId = content?.home_hero_image?.id;

  useEffect(() => {
    if (heroImageId) {
      let media = [];
      try {
        const parsed = JSON.parse(heroImageId);
        media = Array.isArray(parsed) ? parsed : [heroImageId];
      } catch (e) {
        media = [heroImageId];
      }
      if (media.length === 0) media = ['/assets/headers/home_bg.jpg'];
      setHeroMedia(media);
    } else {
      setHeroMedia(['/assets/headers/home_bg.jpg']);
    }
  }, [heroImageId]);

  // Auto-advance hero if not interacted
  useEffect(() => {
    if (heroMedia.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentHeroIndex(prev => (prev + 1) % heroMedia.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroMedia]);

  const nextHeroSlide = () => setCurrentHeroIndex(prev => (prev + 1) % heroMedia.length);
  const prevHeroSlide = () => setCurrentHeroIndex(prev => (prev === 0 ? heroMedia.length - 1 : prev - 1));

  const isVideo = (url) => url && url.match(/\.(mp4|webm|ogg)$/i);

  const getDynamicIcon = (name, defaultIcon) => {
    if (!name) return defaultIcon;
    const n = name.toLowerCase().trim();
    const icons = {
      scissors: <Scissors className="w-6 h-6" />,
      pentool: <PenTool className="w-6 h-6" />,
      award: <Award className="w-6 h-6" />,
      shieldcheck: <ShieldCheck className="w-6 h-6" />,
      briefcase: <Briefcase className="w-6 h-6" />,
      shirt: <Shirt className="w-6 h-6" />,
      layers: <Layers className="w-6 h-6" />,
      clock: <Clock className="w-6 h-6" />,
      truck: <Truck className="w-6 h-6" />,
      factory: <Factory className="w-6 h-6" />,
      package: <Package className="w-6 h-6" />,
      ruler: <Ruler className="w-6 h-6" />,
      palette: <Palette className="w-6 h-6" />,
      zap: <Zap className="w-6 h-6" />,
      users: <Users className="w-6 h-6" />,
      tag: <Tag className="w-6 h-6" />,
      cpu: <Cpu className="w-6 h-6" />,
      globe: <Globe className="w-6 h-6" />
    };
    return icons[n] || defaultIcon;
  };

  const handleGalleryClick = (index) => {
    const galleryItems = gallery.map(item => ({
      url: item.image_path,
      type: item.image_path.match(/\.(mp4|webm|ogg)$/i) ? 'video' : 'image',
      caption: item.caption_id,
      description: item.description_id
    }));
    setMediaLightboxItems(galleryItems);
    setMediaLightboxIndex(index);
    setMediaLightboxOpen(true);
  };

  const handlePartnerClick = (partners, index) => {
    const items = partners.map(p => {
        const isObj = typeof p === 'object';
        const name = isObj ? (p.description || 'Mitra Terpercaya') : 'Mitra Terpercaya';
        const link = isObj ? p.link : '';

        let descHtml = '';
        if (link) {
            descHtml = `<a href="${link}" target="_blank" rel="noopener noreferrer" class="lightbox-link-btn">Kunjungi Website</a>`;
        } else {
            descHtml = '<p class="text-zinc-400 italic">Website tidak tersedia.</p>';
        }

        return {
            url: isObj ? p.image : p,
            type: 'image',
            caption: name,
            description: descHtml
        };
    });
    setMediaLightboxItems(items);
    setMediaLightboxIndex(index);
    setMediaLightboxOpen(true);
  };

    const faqList = [1, 2, 3, 4].map(num => ({
      q: content[`home_faq_${num}_q`]?.id || (num === 1 ? 'Apakah ada minimum order?' : `Pertanyaan ${num}`),
      a: content[`home_faq_${num}_a`]?.id || (num === 1 ? 'Ya, minimum order kami adalah 12 pcs per desain.' : `Jawaban ${num}`)
    }));
  
    // --- SEO & Schema Markup ---
    useEffect(() => {
      // 1. Title & Meta Tags
      document.title = "iMoonusel - Jasa Konveksi Bandung & Seragam Premium";
      
      const setMeta = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('name', name);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };
  
      setMeta("description", "Konveksi Bandung terpercaya untuk pembuatan seragam kerja, kaos, kemeja, dan jaket. Tukang jahit terdekat dengan standar kualitas ekspor dan harga kompetitif.");
      setMeta("keywords", "konveksi bandung, konveksi seragam, konveksi kaos, tukang jahit terdekat, jasa konveksi bandung, konveksi terdekat");
  
      // Open Graph
      const setOg = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('property', property);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };
  
      setOg("og:title", "iMoonusel - Jasa Konveksi Bandung & Seragam Premium");
      setOg("og:description", "Solusi produksi garment terbaik di Bandung. Melayani pembuatan seragam, kaos, dan merchandise dengan kualitas premium.");
      setOg("og:image", "/logo512.png"); // Placeholder, ideally specific banner
      setOg("og:url", window.location.href);
      setOg("og:type", "website");
  
      // 2. JSON-LD Schema Markup (For Sitelinks & Rich Snippets)
      const schemaData = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "name": "iMoonusel Konveksi",
            "url": "https://konveksiimoonusel.com",
            "logo": "https://konveksiimoonusel.com/logo512.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+62-898-7459-759",
              "contactType": "customer service",
              "areaServed": "ID",
              "availableLanguage": "Indonesian"
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Girimukti, Batujajar",
              "addressLocality": "Kabupaten Bandung Barat",
              "addressRegion": "Jawa Barat",
              "postalCode": "40561",
              "addressCountry": "ID"
            }
          },
          {
            "@type": "WebSite",
            "url": "https://konveksiimoonusel.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://konveksiimoonusel.com/gallery?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@type": "FAQPage",
            "mainEntity": faqList.map(item => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a
              }
            }))
          }
        ]
      };
  
      const script = document.createElement('script');
      script.type = "application/ld+json";
      script.innerHTML = JSON.stringify(schemaData);
      document.head.appendChild(script);
  
      return () => {
        // Cleanup schema on unmount to avoid duplicates if navigating back
        document.head.removeChild(script);
      };
    }, [faqList]); // Re-run if FAQ content changes (from API)
  
    if (loading) {    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-xs font-bold uppercase tracking-widest animate-pulse">Memuat Antarmuka...</div>
      </div>
    );
  }

  const activeItem = mediaLightboxOpen && mediaLightboxItems.length > 0 ? mediaLightboxItems[mediaLightboxIndex] : null;

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-blue-600 selection:text-white overflow-x-hidden">

      {/* --- Fullscreen Editorial Modal (Reused from Gallery.js) --- */}
      {activeItem && mediaLightboxOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/95 backdrop-blur-xl animate-in fade-in duration-200">

          {/* Close Button - Clean & Big */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 z-[110] p-3 text-black hover:text-red-600 transition-colors bg-zinc-100 hover:bg-zinc-200 rounded-none border border-zinc-200"
          >
            <X size={24} />
          </button>

          {/* Navigation - Minimalist Arrows */}
          {mediaLightboxItems.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                disabled={mediaLightboxIndex === 0}
                className="absolute left-4 md:left-8 z-[105] p-4 text-black hover:bg-black hover:text-white disabled:opacity-0 transition-all border border-zinc-200 bg-white"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={handleNext}
                disabled={mediaLightboxIndex === mediaLightboxItems.length - 1}
                className="absolute right-4 md:right-8 z-[105] p-4 text-black hover:bg-black hover:text-white disabled:opacity-0 transition-all border border-zinc-200 bg-white"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Main Content Area */}
          <div
            className="relative w-full h-full flex items-center justify-center p-4 md:p-20 overflow-hidden"
            onClick={closeModal}
          >
            <div
              className="relative max-w-full max-h-full transition-transform duration-200 ease-out shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              style={{ transform: `scale(${zoomLevel})` }}
            >
              {(activeItem.type === 'video' || isVideo(activeItem.url)) ? (
                <video
                  src={activeItem.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh] object-contain bg-black"
                />
              ) : (
                <img
                  src={activeItem.url}
                  alt={activeItem.caption}
                  className="max-w-full max-h-[80vh] object-contain select-none bg-white"
                  draggable={false}
                />
              )}
            </div>
          </div>

          {/* Info Toggle Button */}
          <div className="absolute bottom-8 right-8 z-[110] flex gap-4">
             {/* Zoom Controls */}
             <div className="hidden md:flex bg-white border border-zinc-200">
                <button onClick={() => setZoomLevel(z => Math.max(1, z - 0.5))} className="p-3 hover:bg-zinc-100 text-black border-r border-zinc-200"><ZoomOut size={18} /></button>
                <button onClick={() => setZoomLevel(z => Math.min(3, z + 0.5))} className="p-3 hover:bg-zinc-100 text-black"><ZoomIn size={18} /></button>
             </div>

             <button
               onClick={toggleInfo}
               className={`flex items-center gap-2 px-6 py-3 border transition-all duration-300 font-bold text-xs uppercase tracking-widest ${
                 showInfo
                   ? 'bg-black text-white border-black'
                   : 'bg-white text-black border-zinc-200 hover:border-black'
               }`}
             >
               <Info size={16} />
               <span>{showInfo ? 'Tutup Detail' : 'Info Proyek'}</span>
             </button>
          </div>

          {/* Info Sidebar / Overlay */}
          <div
            className={`absolute top-0 right-0 h-full w-full md:w-[500px] bg-white border-l border-zinc-200 shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] z-[108] flex flex-col ${
              showInfo ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 overflow-y-auto p-10 scrollbar-thin scrollbar-thumb-zinc-300">
              {/* Header inside Info Panel */}
              <div className="mb-8 border-b border-black pb-6">
                <SectionTag>Detail Proyek</SectionTag>
                <h2 className="text-3xl font-black text-black leading-tight mb-2 uppercase">
                  {activeItem.caption || "Tanpa Judul"}
                </h2>
              </div>

              {/* Description */}
              <div className="prose prose-zinc max-w-none text-zinc-600 font-serif leading-loose">        
                {activeItem.description ? (
                  <div dangerouslySetInnerHTML={{ __html: activeItem.description }} />
                ) : (
                  <p className="italic text-zinc-400">Tidak ada deskripsi detail.</p>
                )}
              </div>

              {/* Metadata */}
              <div className="mt-12 pt-8 border-t border-zinc-200 grid grid-cols-2 gap-8">
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Tanggal Unggah</span>
                  <span className="text-sm font-bold text-black">{activeItem.created_at ? new Date(activeItem.created_at).toLocaleDateString() : '—'}</span>
                </div>
                <div>
                   <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Tipe Media</span>
                   <span className="text-sm font-bold text-black">{(activeItem.type === 'video' || isVideo(activeItem.url)) ? 'Aset Video' : 'Fotografi'}</span>
                </div>
              </div>
            </div>

            {/* Mobile Close Button (inside panel) */}
            <div className="p-6 border-t border-zinc-200 md:hidden">
              <button
                onClick={() => setShowInfo(false)}
                className="w-full py-4 bg-black text-white font-bold uppercase tracking-widest text-xs"   
              >
                Tutup Detail
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center w-full">
          {/* Text Column */}
          <div className="md:col-span-7 z-10 flex flex-col order-2 md:order-1">
            <SectionTag>Manufaktur Utama</SectionTag>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-6 md:mb-8 uppercase text-black">
              {content.home_headline?.id ? (
                <>
                  {content.home_headline.id.split(' ').slice(0, 3).join(' ')} <br/>
                  <span className="text-zinc-400">{content.home_headline.id.split(' ').slice(3).join(' ')}</span>
                </>
              ) : (
                <>KAMI MEMBANGUN <br/><span className="text-zinc-400">MASA DEPAN</span> <br/>APPAREL.</>     
              )}
            </h1>
            <p className="text-lg md:text-xl text-zinc-800 mb-8 md:mb-10 max-w-lg leading-relaxed font-light">
              {content.home_subheadline?.id || 'Bukan sekadar jahitan. Kami mengintegrasikan presisi teknik dengan material kelas dunia.'}
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <BloombergButton to={content.home_cta_primary_link?.id || '/contact'}>
                {content.home_cta_primary?.id || 'Mulai Proyek'}
              </BloombergButton>
              <BloombergButton to={content.home_cta_secondary_link?.id || '/gallery'} variant="outline">  
                {content.home_cta_secondary?.id || 'Lihat Katalog'}
              </BloombergButton>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-md border-t border-zinc-200 pt-6">      
              <StatItem label={content.home_stat_1_label?.id || 'Klien'} value={content.home_stat_1_value?.id || '24+'} />
              <StatItem label={content.home_stat_2_label?.id || 'Output Harian'} value={content.home_stat_2_value?.id || '1.2k'} />
              <StatItem label={content.home_stat_3_label?.id || 'Efisiensi'} value={content.home_stat_3_value?.id || '98%'} />
            </div>
          </div>

          {/* Image/Carousel Column - Visible on Mobile */}
          <div className="md:col-span-5 relative order-1 md:order-2 mb-8 md:mb-0 -mx-6 md:mx-0">
            <div className="aspect-[4/5] md:aspect-[4/5] bg-zinc-100 overflow-hidden border-0 md:border-[12px] border-zinc-100 shadow-none md:shadow-2xl md:rotate-2 relative group">
              {/* Carousel Content */}
              {heroMedia.map((media, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${index === currentHeroIndex ? 'opacity-100' : 'opacity-0'}`}
                >
                  {isVideo(media) ? (
                    <video src={media} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                  ) : (
                    <img src={media} alt={`Hero ${index}`} className="w-full h-full object-cover" />      
                  )}
                </div>
              ))}

              {/* Carousel Controls */}
              {heroMedia.length > 1 && (
                <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                  <button onClick={prevHeroSlide} className="bg-black/50 text-white p-2 hover:bg-black transition-colors"><ChevronLeft size={16}/></button>
                  <button onClick={nextHeroSlide} className="bg-black/50 text-white p-2 hover:bg-black transition-colors"><ChevronRight size={16}/></button>
                </div>
              )}
            </div>

            {/* Badge - Mobile Optimized */}
            <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-6 bg-blue-600 p-4 md:p-6 text-white max-w-[140px] md:max-w-[180px] -rotate-3 shadow-lg z-10 hidden xs:block">
              <p className="text-xs md:text-sm font-bold uppercase tracking-widest mb-1 md:mb-2 opacity-80">Verifikasi</p>
              <p className="text-sm md:text-base italic leading-tight font-medium">"Jaminan 100% Quality Control pada setiap output."</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TICKER */}
      <Marquee />

            {/* 3. INTEGRITY SECTION (Restored) */}
            <section className="py-16 md:py-24 bg-white">
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 md:gap-16 items-center">        
                <div className="order-2 md:order-1">
                   {content.home_integrity_image?.id ? (
                     <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                       {isVideo(content.home_integrity_image.id) ? (
                          <video src={content.home_integrity_image.id} autoPlay muted loop playsInline className="w-full h-full object-cover transition-all duration-700" />
                       ) : (
                          <img src={content.home_integrity_image.id} alt="Integrity" className="w-full h-full object-cover transition-all duration-700" />
                       )}
                       <div className="absolute inset-0 border border-black/5 pointer-events-none"></div>       
                     </div>
                   ) : (
                     <div className="aspect-square bg-zinc-200 flex items-center justify-center text-zinc-400">Tidak Ada Gambar</div>
                   )}
                </div>
                <div className="order-1 md:order-2">
                  <SectionTag>Filosofi Kami</SectionTag>
                  <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-8 uppercase leading-none">   
                    {content.home_integrity_title?.id || 'Integritas Utama'}
                  </h2>
                  <div className="prose prose-base md:prose-lg text-zinc-800 font-light leading-relaxed mb-8">  
                    <p className="mb-6 text-base md:text-lg">
                      {content.home_integrity_text_1?.id || '11 tahun pengalaman mengajarkan bahwa kendala teknis adalah bagian dari realita manufaktur.'}
                    </p>
                    <p className="text-base md:text-lg">
                      {content.home_integrity_text_2?.id || 'Perbaikan total atau penggantian penuh adalah standar prosedur jika terjadi kelalaian internal.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-zinc-400">
                     <span className="w-12 h-[1px] bg-zinc-300"></span>
                     {content.home_integrity_since?.id || 'EST. 2012'}
                  </div>
                </div>
              </div>
            </section>
      
            {/* 4. PRODUCT CATEGORIES (Visuals) */}
            <section className="py-16 md:py-24 border-t border-zinc-200">
              <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                  <div>
                    <SectionTag>Kategori Produk</SectionTag>
                    <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">{content.home_categories_title?.id || 'Output Spesialis'}</h2>
                  </div>
                  <Link to="/gallery" className="text-sm font-bold uppercase tracking-widest border-b border-black pb-1 hover:text-blue-600 transition-colors">
                    Lihat Semua Kategori
                  </Link>
                </div>
      
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <div key={num} className="group cursor-pointer">
                      <div className="aspect-square bg-zinc-100 mb-4 overflow-hidden relative border border-zinc-100">
                         {content[`cap_${num}_image`]?.id ? (
                            <img
                              src={content[`cap_${num}_image`].id}
                              alt={`Cap ${num}`}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-300 text-xs font-bold uppercase tracking-widest">Placeholder Gambar</div>
                          )}
                      </div>
                      <h4 className="text-sm md:text-lg font-bold uppercase tracking-tight group-hover:text-blue-600 transition-colors">
                        {content[`cap_${num}_title`]?.id || `Kategori ${num}`}
                      </h4>
                    </div>
                  ))}
                </div>
              </div>
            </section>
      
            {/* 5. SERVICES (Technical Grid) */}
            <section className="py-16 md:py-24 bg-zinc-50 border-t border-zinc-200">
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                  <SectionTag className="justify-center">Layanan Teknis</SectionTag>
                  <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">{content.home_services_title?.id || 'Proses Produksi'}</h2>
                </div>
      
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {[
                    { num: 1, defaultIcon: <Scissors />, defaultTitle: 'Cutting & Pattern' },
                    { num: 2, defaultIcon: <PenTool />, defaultTitle: 'Sewing' },
                    { num: 3, defaultIcon: <Award />, defaultTitle: 'Quality Control' },
                    { num: 4, defaultIcon: <Briefcase />, defaultTitle: 'Finishing' }
                  ].map((item) => {
                     const dynamicIconName = content[`service_${item.num}_icon`]?.id;
                     const iconComponent = getDynamicIcon(dynamicIconName, item.defaultIcon);
      
                     return (
                      <div key={item.num} className="p-6 md:p-8 bg-white border border-zinc-200 hover:border-black transition-colors group">
                        <div className="mb-4 md:mb-6 text-zinc-400 group-hover:text-black transition-colors">   
                          {React.cloneElement(iconComponent, { className: "w-6 h-6 md:w-8 md:h-8" })}
                        </div>
                        <h3 className="text-sm md:text-lg font-bold mb-2 md:mb-3 uppercase tracking-tight">     
                          {content[`service_${item.num}_title`]?.id || item.defaultTitle}
                        </h3>
                        <p className="text-xs md:text-sm text-zinc-600 leading-relaxed font-light">
                          {content[`service_${item.num}_desc`]?.id || 'Modul deskripsi layanan standar.'}       
                        </p>
                      </div>
                     );
                  })}
                </div>
              </div>
            </section>
      
            {/* 6. CONSISTENCY (Quality - Dark) */}
            <section className="py-20 bg-black text-white">
              <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row gap-12">
                  <div className="md:w-1/3">
                     <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6 uppercase text-white">{content.home_quality_intro_title?.id || 'Standar Konsistensi'}</h2>
                     <p className="text-zinc-400 font-light leading-relaxed text-sm md:text-base">
                       {content.home_quality_intro_desc?.id || 'Kami menerapkan protokol kontrol kualitas yang ketat. Tidak ada kompromi untuk detail terkecil sekalipun.'}
                     </p>
                  </div>
                  <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-8">
                     {[1, 2, 3, 4].map(num => (
                        <div key={num} className="border-l border-zinc-800 pl-6">
                          <div className="text-2xl font-black text-zinc-700 mb-2">0{num}</div>
                          <h4 className="text-lg font-bold mb-2 text-white">
                            {content[`home_quality_${num}_title`]?.id || 'Cek Kualitas'}
                          </h4>
                          <p className="text-sm text-zinc-400 font-light leading-relaxed">
                            {content[`home_quality_${num}_desc`]?.id || 'Detail deskripsi quality control.'}    
                          </p>
                        </div>
                     ))}
                  </div>
                </div>
              </div>
            </section>
      {/* 7. FEATURED PARTNER (Highlight) */}
      <section className="py-0 border-b border-zinc-200">
        <div className="grid md:grid-cols-2 min-h-[500px]">
          <div className="bg-zinc-100 relative overflow-hidden group h-[400px] md:h-auto">
             {content.home_featured_image?.id && (
               <div
                 className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-105"
                 style={{backgroundImage: `url(${content.home_featured_image.id})`}}
               ></div>
             )}
          </div>
          <div className="bg-white p-10 md:p-20 flex flex-col justify-center">
            <SectionTag>Sorotan</SectionTag>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6 uppercase">
              {content.home_featured_title?.id || 'Dipercaya Figur Publik.'}
            </h2>
            <p className="text-lg md:text-xl text-zinc-800 font-light leading-relaxed mb-8">
              {content.home_featured_desc?.id || 'Kualitas yang diakui oleh tokoh ternama. Dari panggung hiburan hingga arena balap profesional.'}
            </p>
            <div>
              <BloombergButton to="/gallery" variant="outline">
                Lihat Gallery
              </BloombergButton>
            </div>
          </div>
        </div>
      </section>

      {/* 8. THE ARCHIVE (Gallery) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <SectionTag>Arsip</SectionTag>
              <h2 className="text-4xl font-black tracking-tighter uppercase">Karya Terbaru</h2>
            </div>
            <Link to="/gallery" className="hidden md:block text-sm font-bold uppercase tracking-widest border-b border-black pb-1 hover:text-blue-600 transition-colors">
               Lihat Semua Arsip
            </Link>
          </div>

          {/* Mobile: Horizontal Scroll, Desktop: Grid */}
          <div className="flex md:grid md:grid-cols-3 gap-4 overflow-x-auto pb-6 md:pb-0 px-1 md:px-0 scrollbar-hide snap-x">
            {gallery.length > 0 ? gallery.slice(0, 3).map((item, i) => {
               const isVid = isVideo(item.image_path);
               return (
                <div
                  key={item.id} 
                  className="min-w-[85vw] md:min-w-0 aspect-[3/4] relative group overflow-hidden cursor-pointer bg-black snap-center"
                  onClick={() => handleGalleryClick(i)}
                >
                  {isVid ? (
                    <video src={item.image_path} muted loop playsInline autoPlay className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700" />
                  ) : (
                    <img
                      src={item.image_path}
                      alt={item.caption_id}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent flex flex-col justify-end p-6 md:p-8 translate-y-4 group-hover:translate-y-0 transition-transform">
                    <span className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold mb-2">Proyek 0{i+1}</span>
                    <h4 className="text-white text-lg md:text-xl font-bold tracking-tight mb-4 line-clamp-2">
                      {item.caption_id || 'Tanpa Judul'}
                    </h4>
                    <div className="flex items-center gap-2 text-white text-xs font-bold uppercase opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                      Lihat Detail <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
               );
            }) : <div className="p-8 text-center text-zinc-400 w-full col-span-3">Memuat Arsip...</div>}
          </div>

          <div className="mt-6 text-center md:hidden">
             <Link to="/gallery" className="inline-block border-b border-black pb-1 text-xs font-bold uppercase tracking-widest">
               Lihat Semua Arsip
             </Link>
          </div>
        </div>
      </section>

      {/* 9. LIMITS (Specialization) */}
      <section className="py-16 md:py-24 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
           <h2 className="text-2xl md:text-3xl font-black uppercase mb-6">{content.home_limits_title?.id || 'Spesialisasi & Batasan'}</h2>
           <p className="text-base md:text-lg text-zinc-800 leading-relaxed max-w-2xl mx-auto mb-12">     
             {content.home_limits_intro?.id || 'Kami menetapkan standar untuk menjaga kualitas.'}
           </p>
           <div className="grid md:grid-cols-3 gap-8 text-left">
             {[1, 2, 3].map(num => (
               <div key={num} className="bg-white p-6 border border-zinc-200">
                  <h3 className="text-sm font-bold mb-2 text-black uppercase tracking-wide">
                    {content[`home_limits_${num}_title`]?.id || `Standar ${num}`}
                  </h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">
                    {content[`home_limits_${num}_desc`]?.id || 'Deskripsi batasan.'}
                  </p>
               </div>
             ))}
           </div>
        </div>
      </section>

      {/* 10. PARTNERS */}
      <section className="py-12 md:py-16 bg-blue-600">
         <div className="max-w-7xl mx-auto px-6">
           <div className="text-center text-xs font-bold uppercase tracking-[0.2em] text-white/80 mb-8">  
             Mitra Terpercaya
           </div>
           <div className="grid grid-cols-3 md:grid-cols-6 gap-6 md:gap-12 items-center transition-all duration-500">
              {(() => {
                  let partners = [];
                  try {
                      const raw = content.home_partners?.id || content.partners_gallery?.id;
                      if (Array.isArray(raw)) partners = raw;
                      else if (typeof raw === 'string') partners = JSON.parse(raw);
                  } catch (e) { partners = []; }

                  if (partners.length === 0) return <div className="col-span-full text-center text-xs text-zinc-300"></div>;

                  return partners.map((item, idx) => {
                      const imgUrl = typeof item === 'object' ? item.image : item;
                      return (
                          <div key={idx} className="flex justify-center p-2 cursor-pointer" onClick={() => handlePartnerClick(partners, idx)}>
                              <img src={imgUrl} alt={`Partner ${idx}`} className="max-w-[100px] md:max-w-[140px] w-auto h-auto object-contain hover:scale-105 transition-transform" />
                          </div>
                      );
                  });
              })()}
           </div>
         </div>
      </section>

      {/* 11. ARTICLES */}
      <section className="py-16 md:py-24 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight uppercase">Wawasan Terbaru</h2> 
            <Link to="/articles" className="text-xs md:text-sm font-bold uppercase tracking-widest border-b border-black pb-1">Semua Artikel</Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {articles.slice(0, 3).map(art => (
              <Link to={`/articles/${art.slug}`} key={art.id} className="group block">
                 <div className="aspect-[3/2] bg-zinc-100 overflow-hidden mb-4 relative">
                    <img
                      src={art.featured_image}
                      alt={art.title_id}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/600x400?text=No+Image'}}
                    />
                    <div className="absolute top-2 left-2 bg-white px-2 py-1 text-xs font-bold uppercase tracking-widest">
                       {new Date(art.created_at).toLocaleDateString()}
                    </div>
                 </div>
                 <h3 className="text-lg md:text-xl font-bold leading-tight mb-2 group-hover:text-blue-800 transition-colors line-clamp-2">
                   {art.title_id}
                 </h3>
                 <p className="text-sm text-zinc-600 leading-relaxed line-clamp-2 mb-4">
                   {art.excerpt}
                 </p>
                 <div className="text-xs font-bold uppercase tracking-widest flex items-center gap-1 group-hover:gap-2 transition-all text-blue-600">
                   Baca Artikel <ArrowRight size={10} />
                 </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 12. FAQ */}
      <section className="py-16 md:py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-3xl mx-auto px-6">
           <div className="text-center mb-12">
             <SectionTag className="justify-center">Dukungan</SectionTag>
             <h2 className="text-2xl md:text-3xl font-black tracking-tight uppercase">{content.home_faq_title?.id || 'FAQ'}</h2>
           </div>

           <div className="space-y-0 bg-white border border-zinc-200">
             {faqList.map((item, index) => (
               <div key={index} className="border-b border-zinc-200 last:border-0">
                  <button
                    className="w-full py-5 px-6 flex justify-between items-center text-left hover:bg-zinc-50 transition-colors"
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                  >
                    <span className="text-sm md:text-base font-bold tracking-tight pr-4">{item.q}</span>  
                    {faqOpen === index ? <Minus size={16} /> : <Plus size={16} />}
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out px-6 ${faqOpen === index ? 'max-h-48 opacity-100 pb-5' : 'max-h-0 opacity-0'}`}
                  >
                     <p className="text-zinc-600 font-light leading-relaxed text-sm md:text-base">        
                       {item.a}
                     </p>
                  </div>
               </div>
             ))}
           </div>
        </div>
      </section>

      {/* 13. READY TO SCALE (CTA) */}
      <section className="py-20 md:py-32 bg-white text-center border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag className="justify-center">Siap Berkembang?</SectionTag>
          <h2 className="text-4xl md:text-7xl font-black tracking-tighter mb-6 uppercase">
            {content.home_cta_footer_title?.id || 'Mulai Produksi.'}
          </h2>
          <p className="text-xl text-zinc-800 mb-10 max-w-2xl mx-auto font-light leading-relaxed">        
            {content.home_cta_footer_desc?.id || 'Konsultasikan kebutuhan brand kamu dengan tim ahli kami.'}
          </p>
          <div className="flex justify-center gap-6">
            <BloombergButton to="/contact" className="py-4 px-10 text-lg">
              {content.home_cta_footer_btn?.id || 'Konsultasi Sekarang'}
            </BloombergButton>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;